import {
  IBrand,
  IItem,
  Invoice,
  InvoiceLine,
  IUnit,
  LineDataBySupplier,
  SalesTax,
  IItemSupplierVariant,
  SelectOption,
  IInvoice,
  IInvoiceLine,
  IPurchaseOrder,
  IPurchaseOrderLine,
  ISupplier,
  ItemSupplierVariant,
  IStockCost,
} from '$models'
import { UiInvoice } from './uiInvoice'
import { exceptionMessageHandler, findMatching, roundNumber } from '$utils'
import { Box } from '@mui/material'
import dayjs from 'dayjs'
import { UiInvoiceLine } from './uiInvoiceLine'
import { New as NewIcon } from 'src/components/common/icons'
import { InfoOutlined as InfoIcon } from '@mui/icons-material'
import {
  applyDiscount,
  calculateFooterTotals,
  calculateTaxOnItem,
} from '../../../_common/helpers/helper'
import {
  API_DATE_FORMAT,
  DocumentType,
  MatchedOnType,
  InvoiceVersion,
} from '$constants'

export const matchesMinWidth1100 = '(min-width:1100px)'

export const longConentTextFieldSxProps = {
  paddingRight: '0rem !important',
  '.MuiInputBase-input': {
    textOverflow: 'ellipsis',
  },
}

export const longContentContainerSxProps = {
  maxWidth: '100%',
}

export const transformInvoiceLinesToGridInvoiceLines = (
  invoiceLines: IInvoiceLine[],
  salesTax: SalesTax[],
  lineItemsIncludeTax: boolean
): UiInvoiceLine[] => {
  return invoiceLines.map((line, index) =>
    invoiceLineToGridInvoiceLine(line, index, salesTax, lineItemsIncludeTax)
  )
}

/**
 * Transform an invoice objecto into the object that we need for the invoice form
 * @param invoice
 * @param linkedPurchaseOrder
 * @param suppliers
 * @param items
 * @returns UiInvoice
 */
export const transformApiInvoiceToUiInvoice = (
  invoice: IInvoice,
  linkedPurchaseOrder: IPurchaseOrder | null,
  suppliers: Record<string, ISupplier> | null
): UiInvoice => {
  // if the supplier nname of the invoice doesn't equal the purchase order supplier name
  // we use the purchase order supplier name instead and a warning icon to the text input
  let linkedSupplierId = invoice?.linkedSupplierId
  let supplierName = invoice?.supplierName

  // If the supplier name is there but not the supplier id, we try to get the supplier id
  // from the system suppliers list
  if (!linkedSupplierId && supplierName && suppliers) {
    linkedSupplierId = findMatching(suppliers, 'name', supplierName)?.id
  }

  if (
    linkedPurchaseOrder?.supplierId &&
    linkedPurchaseOrder?.supplierId !== invoice?.linkedSupplierId
  ) {
    linkedSupplierId = linkedPurchaseOrder?.supplierId
    supplierName = linkedPurchaseOrder?.supplierName
  }

  return {
    ...invoice,
    lines: [],

    // If the invoice comes with a value in purchaseOrderNumber that does not match with any purchase order
    // in the system, then we should display nothing in the Order Number field - so we can tell that if there is a
    // linkedPurchaseOrder then show the order number from the linkedPurchaseOrder, otherwise show it as empty
    purchaseOrderNumber: linkedPurchaseOrder?.orderNumber,

    linkedSupplierId: linkedSupplierId,
    supplierName: supplierName,
    receivedAt: invoice?.receivedAt ?? dayjs(),
  }
}

/**
 * Update the invoice lines that match the supplier variants
 * @param invoiceLines
 * @param items
 * @param brands
 * @param units
 * @param linkedSupplierId
 * @param unitCostIncludesTax
 * @returns
 */
export const updateInvoiceLinesWithSupplierVariants = (
  invoiceLines: UiInvoiceLine[],
  items: Record<string, IItem>,
  brands: Record<string, IBrand> | null,
  units: Record<string, IUnit>,
  linkedSupplierId: string,
  unitCostIncludesTax: boolean,
  salesTax: SalesTax[]
) => {
  // Update line if item matches on code.
  // Ensures changes made by the user are reflected, similar to backend checks during invoice scanning.
  // We need to do this here as the values could change after the invoice is scanned
  invoiceLines.forEach((line) => {
    if (line.code) {
      const matchedVariant = findSupplierVariantByCode(
        items,
        line.code,
        linkedSupplierId
      )

      if (matchedVariant) {
        const { stockItemId, brandId, unitId } = matchedVariant
        const matchedItem = stockItemId ? items[stockItemId] : undefined
        const matchedBrand = brandId ? brands?.[brandId] : undefined
        const matchedUnit = unitId ? units?.[unitId] : undefined

        const { saleTaxRate, customTaxRate, selectedSalesTax, taxAmount } =
          calculateTaxRelatedFields(
            matchedItem,
            line.unitCost,
            line.quantityReceived,
            salesTax,
            unitCostIncludesTax
          )

        line.itemMatchedOn = MatchedOnType.Code
        line.description = matchedItem?.name
        line.linkedItemId = matchedItem?.id
        line.brand = matchedBrand?.name
        line.linkedBrandId = matchedBrand?.id
        line.unit = matchedUnit?.name
        line.linkedUnitId = matchedUnit?.id
        line.taxAmount = roundNumber(taxAmount, 2)
        line.saleTaxRate = saleTaxRate
        line.selectedSalesTax = selectedSalesTax
        line.customTaxRate = customTaxRate
      } else {
        if (line.unitCost && line.quantityReceived && line.saleTaxRate) {
          const newTaxAmount = calculateTaxOnItem(
            line.unitCost,
            line.quantityReceived,
            line.saleTaxRate,
            false // gstInclusive = false here because the value stored in the DB is GST Exclusive
          )

          line.taxAmount = roundNumber(newTaxAmount, 2)
        }
      }
    }
  })
}

/**
 * Gets the default brand and ordering unit of an item for a given row and item
 * @param item the item object the user has selected
 * @param supplierId the supplierId of the invoice
 * @param brands all possible brands
 * @param units all possible units
 * @returns object with properties brandId, brandName, unitId, unitName that are undefined or string
 */
export const getDefaultValuesFromItem = (
  item: IItem | undefined,
  supplierId: string | undefined,
  brands: Record<string, IBrand>,
  units: Record<string, IUnit>
) => {
  const result: {
    code?: string
    brandId?: string
    brandName?: string
    unitId?: string
    unitName?: string
    unitRatio?: number
  } = {
    code: undefined,
    brandId: undefined,
    brandName: undefined,
    unitId: undefined,
    unitName: undefined,
    unitRatio: undefined,
  }

  if (!item) return result

  // item.supplier is item variants (NOT suppliers) and each variant has a supplierId linked to it
  // so we need to check if there is a variant for the current supplierId
  const supplierVariants = supplierId
    ? item.suppliers?.filter((i) => i.supplierId == supplierId) ?? []
    : []

  // get the default item per supplier using defaultForSupplier if is there any.
  let defaultItemForSupplier: IItemSupplierVariant | undefined = undefined
  if (supplierVariants.length > 0) {
    // There should be one default variant for that supplier, but if there is no default then
    // we use the first variant in the list
    defaultItemForSupplier =
      supplierVariants.find(
        (itemSupplierVariant) => itemSupplierVariant.defaultForSupplier
      ) ?? supplierVariants[0]
  }

  // If we found a variant then we use it, otherwise we use the item defaults
  if (defaultItemForSupplier) {
    result.code = defaultItemForSupplier?.stockCode
    result.brandId = defaultItemForSupplier?.brandId
    result.brandName = defaultItemForSupplier?.brandId
      ? brands[defaultItemForSupplier?.brandId]?.name
      : undefined
    result.unitId = defaultItemForSupplier?.unitId
    result.unitName = defaultItemForSupplier?.unitId
      ? units[defaultItemForSupplier?.unitId]?.name
      : undefined
    result.unitRatio = defaultItemForSupplier?.unitId
      ? units[defaultItemForSupplier?.unitId]?.ratio
      : undefined
  } else {
    // Items don't have codes, so we leave it as undefined
    result.code = undefined
    result.brandId = item.defaultBrandId
    result.brandName = item?.defaultBrandId
      ? brands[item.defaultBrandId]?.name
      : undefined
    result.unitId = item.orderingUnitId
    result.unitName = item.orderingUnitId
      ? units[item.orderingUnitId]?.name
      : undefined
    result.unitRatio = item.orderingUnitId
      ? units[item.orderingUnitId]?.ratio
      : undefined
  }

  return result
}

/**
 * Retrieves the default values from a variant
 * @param variant the variant to get values from
 * @param brands all possible brands
 * @param units all possible units
 * @returns object with properties brandId, brandName, unitId, unitName that are undefined or string
 */
export const getDefaultValuesFromVariant = (
  variant: IItemSupplierVariant | undefined,
  brands: Record<string, IBrand>,
  units: Record<string, IUnit>
) => {
  const result: {
    code?: string
    brandId?: string
    brandName?: string
    unitId?: string
    unitName?: string
    unitRatio?: number
  } = {
    code: undefined,
    brandId: undefined,
    brandName: undefined,
    unitId: undefined,
    unitName: undefined,
    unitRatio: undefined,
  }

  result.code = variant?.stockCode

  if (variant?.brandId) {
    result.brandId = variant.brandId
    result.brandName = brands[variant.brandId]?.name
  }

  if (variant?.unitId) {
    result.unitId = variant.unitId
    result.unitName = units[variant.unitId]?.name
    result.unitRatio = units[variant.unitId]?.ratio
  }

  return result
}

/**
 * Valitdates the an Invoice Form and returns errors in a Set
 * @param invoiceForm the values of the invoice form to be validated
 * @returns A Set<string> of all errors
 */
export const validateInvoice = (
  invoiceForm: UiInvoice,
  invoiceLines: UiInvoiceLine[]
): Set<string> => {
  const errors: Set<string> = new Set<string>()

  if (!invoiceForm?.linkedSupplierId) {
    errors.add(
      'The supplier for this invoice is not currently in Loaded. Please change the supplier or create a new one.'
    )
  }

  for (const line of invoiceLines) {
    // Skip deleted lines
    if (line.deletedAt) continue

    checkItemErrors(line, errors)
    checkBrandErrors(line, errors)
    checkUnitErrors(line, errors)
  }

  if (!invoiceForm.receivedAt) {
    errors.add('Please enter a received date.')
  }

  const { total, roundingAmount } = calculateFooterTotals(
    invoiceLines,
    invoiceForm?.unitCostIncludesTax ?? false,
    invoiceForm?.total,
    invoiceForm?.discountAmount
  )

  if (
    total !== invoiceForm.total &&
    !roundingAmount // It's fine for the totals to be different if they are within a rounding error
  ) {
    errors.add(
      'The invoice line totals do not match the invoice total. Please update the invoice lines or invoice total.'
    )
  }

  return errors
}

/**
 * Formats an Invoice object to be ready to be saved in the backend
 * @param invoice the invoice to be saved
 * @returns Invoice ready to be sent to the backend
 */
export const transformUiInvoiceOnSave = (
  invoice: UiInvoice,
  lines: UiInvoiceLine[]
): Invoice => {
  return new Invoice({
    ...invoice,
    dueAt: invoice.dueAt
      ? dayjs(invoice.dueAt).format(API_DATE_FORMAT)
      : undefined,
    issuedAt: invoice.issuedAt
      ? dayjs(invoice.issuedAt).format(API_DATE_FORMAT)
      : undefined,
    lines: lines
      //The invoice lines should not be filtered here
      //even if it is linked to a PO
      .toSorted((a, b) => a.index - b.index)
      .map((line) => {
        const unitCost = line.unitCost ?? 0
        const saleTaxRate = line.saleTaxRate ?? 0

        // The unitCost needs to always be tax exclusive in the database
        // If the current value is tax inclusive we need to perform a calculation to subtract the tax from it
        // quantityReceived = 1 because the unitCost is per item, not total
        const taxExclusiveUnitCost = invoice.unitCostIncludesTax
          ? unitCost - calculateTaxOnItem(unitCost, 1, saleTaxRate, true)
          : unitCost

        return new InvoiceLine({
          ...line,
          code: line.code === '' ? undefined : line.code,
          quantityReceived: line.quantityReceived ?? 0,
          unitCost: applyDiscount(taxExclusiveUnitCost, line.discountPercent),
          taxAmount: applyDiscount(line.taxAmount, line.discountPercent),
          totalCost: applyDiscount(line.totalCost, line.discountPercent),
        })
      }),
  })
}

/**
 * Produces a message to be shown in a tooltip explaining that the value
 * in an input is different than what it was on the original document.
 * @param fieldName
 * @param originalValue
 * @returns
 */
export function buildFieldAlteredTooltipMessage(
  fieldName: string,
  originalValue: string | undefined
) {
  return (
    <Box textAlign={'center'} p={1}>
      <p>This {fieldName} differs from what we found on the invoice.</p>
      <p>Recognised Value: {originalValue ?? 'Not Set'}</p>
    </Box>
  )
}

/**
 * Produces a message to be shown in a tooltip explaining that the value
 * matches a Loaded property (e.g. a supplier stock code) exactly.
 * @param fieldName
 * @param loadedPropertyName
 * @returns
 */
export function buildFieldMatchesTooltipMessage(
  fieldName: string,
  loadedPropertyName: string
) {
  return (
    <Box textAlign={'center'} p={1}>
      <p>
        This {fieldName} matches {loadedPropertyName} exactly.
      </p>
    </Box>
  )
}

/**
 * Produces a message to be shown in a tooltip explaining that the value
 * doesn't exist in Loaded currently.
 * @param fieldName
 * @param loadedPropertyName
 * @returns
 */
export function buildFieldIsNewTooltipMessage(fieldName: string) {
  return (
    <Box textAlign={'center'} p={1}>
      <p>This {fieldName} is not currently set up in this venue.</p>
    </Box>
  )
}

/**
 * Extracts all line data variants and puts them into a dictionary sorted by supplier ID
 * @param items all items in Loaded
 * @param brands all brands in Loaded
 * @param units all units in Loaded
 * @param saleTaxRates the company's sale tax rates
 * @returns
 */
export function extractLineDataVariants(
  items: Record<string, IItem> | null,
  brands: Record<string, IBrand> | null,
  units: Record<string, IUnit> | null,
  saleTaxRates: SalesTax[]
): LineDataBySupplier {
  if (!items) return {}

  return Object.values(items).reduce((accumulator, item) => {
    if (!item.suppliers || !item.name) return accumulator

    // Suppliers = Variants
    item.suppliers.forEach((variant) => {
      if (!variant.supplierId) return

      const brand =
        variant.brandId && brands ? brands[variant.brandId] : undefined
      const unit = variant.unitId && units ? units[variant?.unitId] : undefined
      const saleTaxRate = extractSaleTaxRate(
        saleTaxRates,
        item.globalSalesTaxSortOrder
      )

      const variants = accumulator[variant.supplierId] ?? []

      accumulator[variant.supplierId] = [
        ...variants,
        {
          ...variant,
          itemName: item.name!,
          // Note: using the brand/unit ?.id here as if not brand or unit found then wan these to be undefined
          // Its possible for variants to be using deleted brands and/or units
          brandId: brand?.id,
          brandName: brand?.name,
          unitId: unit?.id,
          unitName: unit?.name,
          saleTaxRate,
          selectedSalesTax: item.globalSalesTaxSortOrder ?? -1,
        },
      ]
    })

    return accumulator
  }, {} as LineDataBySupplier)
}

/**
 * Extract the sale tax rate for an item
 * @param saleTaxRates The company's sale tax rates
 * @param globalSalesTaxSortOrder the globalSalesTaxSortOrder of the item being matched
 * @returns the saleTaxRate of the item, or undefined of not found
 */
export function extractSaleTaxRate(
  saleTaxRates: SalesTax[],
  globalSalesTaxSortOrder: number | undefined
): number | undefined {
  return saleTaxRates.find((rate) => rate.sortOrder === globalSalesTaxSortOrder)
    ?.rate
}

/**
 * Calculate tax related fields for an invoice line
 * @param item
 * @param unitCost
 * @param quantityReceived
 * @param salesTax
 * @returns object { saleTaxRate: number,  customTaxRate: number, selectedSalesTax: number, taxAmount: number }
 */
export const calculateTaxRelatedFields = (
  item: IItem | undefined,
  unitCost: number = 0,
  quantityReceived: number = 0,
  salesTax: SalesTax[] = [],
  taxInclusive: boolean
) => {
  const saleTaxRate = item
    ? extractSaleTaxRate(salesTax, item.globalSalesTaxSortOrder)
    : extractSaleTaxRate(salesTax, 1) // Default to the tax rate in order = 1 as this is the default tax rate for the company
  const customTaxRate = (saleTaxRate ?? 0) * 100
  const selectedSalesTax = item?.globalSalesTaxSortOrder ?? 1
  const taxAmount = calculateTaxOnItem(
    unitCost,
    quantityReceived,
    saleTaxRate ?? 0, // If saleTaxRate is undefined then we assume there is no tax
    taxInclusive
  )

  return {
    saleTaxRate: saleTaxRate ?? 0,
    customTaxRate,
    selectedSalesTax,
    taxAmount,
  }
}

/**
 * Checks a line for any item errors, will add an error to the errors set if so
 * @param line The line to check
 * @param errors The set or errors
 */
const checkItemErrors = (line: IInvoiceLine, errors: Set<string>) => {
  const singleItemError =
    'There is a new item in your invoice lines which must be resolved. Please match to existing records or create a new record before receiving.'
  const multipleItemErrors =
    'There are new items in your invoice lines which must be resolved. Please match to existing records or create a new record before receiving.'
  if (!line.linkedItemId) {
    if (!errors.has(singleItemError) && !errors.has(multipleItemErrors)) {
      errors.add(singleItemError)
    } else {
      errors.delete(singleItemError)
      errors.add(multipleItemErrors)
    }
  }
}

/**
 * Checks a line for any brand errors, will add an error to the errors set if so
 * @param line The line to check
 * @param errors The set or errors
 */
const checkBrandErrors = (line: IInvoiceLine, errors: Set<string>) => {
  const singleBrandError =
    'There is a new brand in your invoice lines which must be resolved. Please match to existing records or create a new record before receiving.'
  const multipleBrandErrors =
    'There are new brands in your invoice lines which must be resolved. Please match to existing records or create a new record before receiving.'
  // Brands are not mandatory thus are able to be undefined
  if (line.brand != null && line.brand != '' && !line.linkedBrandId) {
    if (!errors.has(singleBrandError) && !errors.has(multipleBrandErrors)) {
      errors.add(singleBrandError)
    } else {
      errors.delete(singleBrandError)
      errors.add(multipleBrandErrors)
    }
  }
}

/**
 * Checks a line for any unit errors, will add an errors to the errors set if so
 * @param line The line to check
 * @param errors The set or errors
 */
const checkUnitErrors = (line: IInvoiceLine, errors: Set<string>) => {
  const singleUnitError =
    'There is a new unit in your invoice lines which must be resolved. Please match to existing records or create a new record before receiving.'
  const multipleUnitErrors =
    'There are new units in your invoice lines which must be resolved. Please match to existing records or create a new record before receiving.'
  if (!line.linkedUnitId) {
    if (!errors.has(singleUnitError) && !errors.has(multipleUnitErrors)) {
      errors.add(singleUnitError)
    } else {
      errors.delete(singleUnitError)
      errors.add(multipleUnitErrors)
    }
  }
}

/**
 * Strips lines that originated from a purchase order from a list.
 *  - Lines that do not match with a corrosponding line in the invoice are removed completely
 *  - Lines that were created by combining a PO and Invoice line are reconfigured to have only the invoice as an origin.
 * @param lines
 * @returns An array of lines all originating from an invoice only.
 */
export const removePurchaseOrderLines = (
  lines: UiInvoiceLine[]
): UiInvoiceLine[] =>
  lines
    .filter((line) => line.isInvoiceLine)
    .map((line, idx) => ({
      ...line,
      index: idx,
      isPurchaseOrderLine: false,
      lineClass: function () {
        return invoiceRowClass(this)
      },
    }))

/**
 * Combines the two supplied  lists of purchase order and invoice lines into one set of lines for display.
 *
 * The order of lines is defined in LOADED-6357:
 *   Invoice lines appear in the order they are scanned
 *   All purchase order lines appear below invoice lines
 *   EXCEPT:
 *     1) If a purchase order line has the same stock code as an invoice line, these are combined (in the invoice line position).
 *     2) If a purchase order line has no stock code, and there is an invoice line with no stock code, but the same item and unit, then they are be combined
 *     3) If a purchase order line has the same item as an invoice line, but a different code, it appears under it.
 *
 * @param invoiceLines
 * @param purchaseOrderLines
 * @param salesTax A list of sales tax rates. Used to set the tax rate on lines
 * @param lineItemsIncludeTax `true` if tax is included, `false` if excluded.
 * @param items A list of all items that lines could be linked to.
 * @returns A combined list of lines for use in UI components.
 */
export const combineWithPurchaseOrderLines = (
  invoiceLines: IInvoiceLine[],
  purchaseOrderLines: IPurchaseOrderLine[],
  salesTax: SalesTax[],
  lineItemsIncludeTax: boolean,
  items: Record<string, IItem>,
  isFromPurchaseOrder: boolean = false
): UiInvoiceLine[] => {
  const gridLines: UiInvoiceLine[] = []

  const poLinesByCode = purchaseOrderLines.reduce((dict, poLine) => {
    if (poLine.itemCode) {
      dict[poLine.itemCode.toLocaleLowerCase()] = poLine
    }
    return dict
  }, {})

  const poLinesByItem = purchaseOrderLines.reduce((dict, poLine) => {
    if (poLine.itemId) {
      dict[poLine.itemId] = dict[poLine.itemId]
        ? [...dict[poLine.itemId], poLine]
        : [poLine]
    }
    return dict
  }, {})

  const visitedPoLines: { [key: string]: boolean } = {}

  let index: number = 0

  invoiceLines.forEach((invoiceLine) => {
    // 1) A purchase order line has the same stock code as this invoice line
    let matchingPoLine: IPurchaseOrderLine | undefined = undefined

    if (invoiceLine.code) {
      const potentialMatch = poLinesByCode[invoiceLine.code.toLocaleLowerCase()]

      // Only match by code if the itemIds are compatible (both undefined, or both match)
      // This prevents incorrect matching when codes are identical but refer to different items
      // (e.g., multiple lines with code "Not Set", or duplicate supplier codes for different items)
      // This will be addressed in the LOADED-7907
      if (
        potentialMatch &&
        (!invoiceLine.linkedItemId ||
          !potentialMatch.itemId ||
          invoiceLine.linkedItemId === potentialMatch.itemId)
      ) {
        matchingPoLine = potentialMatch
      }
    }

    if (!matchingPoLine && invoiceLine.linkedItemId && !invoiceLine.code) {
      // 2) If a purchase order line has no stock code, and there is an invoice line with no stock code, but the same item and unit
      matchingPoLine = poLinesByItem[invoiceLine.linkedItemId]?.find(
        (pol: IPurchaseOrderLine) => pol.unitId == invoiceLine.linkedUnitId
      )
    }

    // A purchase order line can only be matched to one invoice line
    const gridInvoiceLine: UiInvoiceLine =
      matchingPoLine && !visitedPoLines[matchingPoLine.id!]
        ? mergeLines(
            invoiceLine,
            matchingPoLine,
            index++,
            salesTax,
            lineItemsIncludeTax,
            items
          )
        : invoiceLineToGridInvoiceLine(
            invoiceLine,
            index++,
            salesTax,
            lineItemsIncludeTax
          )

    if (matchingPoLine) {
      visitedPoLines[matchingPoLine.id!] = true
    }
    gridLines.push(gridInvoiceLine)

    if (invoiceLine.linkedItemId) {
      //3) If a purchase order line has the same item as an invoice line, but a different code, it appears under it.
      ;((poLinesByItem[invoiceLine.linkedItemId] ?? []) as IPurchaseOrderLine[])
        .filter((poLine) => !visitedPoLines[poLine.id!])
        .forEach((poLine) => {
          gridLines.push(
            poLineToGridInvoiceLine(
              poLine,
              index++,
              salesTax,
              items,
              isFromPurchaseOrder,
              invoiceLines.length > 0 // hasExistingInvoiceLines - true if linking existing invoice to PO
            )
          )
          visitedPoLines[poLine.id!] = true
        })
    }
  })

  return gridLines.concat(
    purchaseOrderLines
      .filter((poLine) => !visitedPoLines[poLine.id!])
      .map((poLine) =>
        poLineToGridInvoiceLine(
          poLine,
          index++,
          salesTax,
          items,
          isFromPurchaseOrder,
          invoiceLines.length > 0 // hasExistingInvoiceLines - true if linking existing invoice to PO
        )
      )
  )
}

/**
 * Transforms an API purchase order line into an invoice line that can be used in the presentation layer
 *
 * @param purchaseOrderLine The line to transform
 * @param index The order in the presentation array that this line will appear
 * @param salesTax A list of sales tax rates. Used to set the tax rate on lines
 * @param items A dictionary of all items that line could be linked to.
 * @returns
 */
const poLineToGridInvoiceLine = (
  purchaseOrderLine: IPurchaseOrderLine,
  index: number,
  salesTax: SalesTax[],
  items: Record<string, IItem>,
  isFromPurchaseOrder: boolean = false,
  hasExistingInvoiceLines: boolean = false
): UiInvoiceLine => {
  const selectedSalesTax =
    salesTax.find((tax) => tax.rate === purchaseOrderLine.taxPercent)
      ?.sortOrder ?? -1
  const customTaxRate = (purchaseOrderLine?.taxPercent ?? 0) * 100
  const quantity =
    purchaseOrderLine.quantityReceived ?? purchaseOrderLine.quantityOrdered ?? 0
  const unitCost = purchaseOrderLine.unitCost ?? 0
  const taxPercent = purchaseOrderLine.taxPercent ?? 0

  return {
    code: purchaseOrderLine.itemCode,
    description: purchaseOrderLine.itemId
      ? items[purchaseOrderLine.itemId]?.name
      : '',
    linkedItemId: purchaseOrderLine.itemId,
    brand: purchaseOrderLine.brandName,
    linkedBrandId: purchaseOrderLine.brandId,
    unit: purchaseOrderLine.unitName,
    linkedUnitId: purchaseOrderLine.unitId,
    linkedUnitRatio: purchaseOrderLine.unitRatio,
    quantityOrdered: purchaseOrderLine.quantityOrdered,
    isPurchaseOrderLine: true,
    isInvoiceLine: false,
    isNew: isFromPurchaseOrder, // Set to true for new invoices to show loading state
    lineClass: () => '',
    // Unmerged PO lines quantityReceived logic:
    // - If creating NEW invoice from PO (no existing invoice lines): default to quantity (quantityOrdered)
    // - If linking EXISTING invoice to PO (has invoice lines): 0 (not on the invoice, so not received)
    quantityReceived:
      isFromPurchaseOrder && !hasExistingInvoiceLines ? quantity : 0,
    unitCost: isFromPurchaseOrder ? 0 : roundNumber(unitCost, 2),
    totalCost: isFromPurchaseOrder ? 0 : roundNumber(quantity * unitCost, 2),
    taxAmount: isFromPurchaseOrder
      ? 0
      : roundNumber(quantity * unitCost * taxPercent, 2),
    saleTaxRate: purchaseOrderLine.taxPercent,
    selectedSalesTax: selectedSalesTax,
    customTaxRate: customTaxRate,
    id: window.crypto.randomUUID(),
    index: index++,
  }
}

/**
 * Transforms an API invoice order line into an invoice line that can be used in the presentation layer
 *
 * @param line The line to transform
 * @param index The order in the presentation array that this line will appear
 * @param salesTax A list of sales tax rates. Used to set the tax rate on lines
 * @param lineItemsIncludeTax `true` if tax is included, `false` if excluded.
 * @returns
 */
const invoiceLineToGridInvoiceLine = (
  line: IInvoiceLine,
  index: number,
  salesTax: SalesTax[],
  lineItemsIncludeTax: boolean
): UiInvoiceLine => {
  // Reverse any discount
  let fullUnitCost = (line.unitCost ?? 0) / (1 - (line.discountPercent ?? 0))
  const fullTaxAmount =
    (line.taxAmount ?? 0) / (1 - (line.discountPercent ?? 0))

  // For backwards compatibility - totalCost used to be always calculated, now it's explicitly set
  const discountedTotal =
    line.totalCost || (line.quantityReceived ?? 0) * (line.unitCost ?? 0)
  const fullTotal = (discountedTotal ?? 0) / (1 - (line.discountPercent ?? 0))

  if (lineItemsIncludeTax && fullUnitCost > 0 && line.saleTaxRate) {
    // quantityReceived = 1 because the unitCost is per item, not total
    // gstInclusive = false here because the value stored in the DB is GST Exclusive
    fullUnitCost += calculateTaxOnItem(fullUnitCost, 1, line.saleTaxRate, false)
  }

  const gridInvoiceLine: UiInvoiceLine = {
    ...line,
    id: line.id ?? crypto.randomUUID(),
    index,
    unit: line.unit ?? '',
    selectedSalesTax:
      salesTax.find((tax) => tax.rate === line.saleTaxRate)?.sortOrder ?? -1,
    customTaxRate: (line?.saleTaxRate ?? 0) * 100,
    isPurchaseOrderLine: false,
    isInvoiceLine: true,
    unitCost: roundNumber(fullUnitCost, 2),
    totalCost: roundNumber(fullTotal, 2),
    quantityReceived: roundNumber(line.quantityReceived ?? 0, 3),
    taxAmount: roundNumber(fullTaxAmount, 2),
    lineClass: function () {
      return invoiceRowClass(this)
    },
  }

  return gridInvoiceLine
}

/**
 * Combines an API invoice order line and an API Purchase Order line into an
 * invoice line that can be used in the presentation layer
 *
 * @param invoiceLine The invoice line to transform
 * @param poLine The purchase order line to transform
 * @param index The order in the presentation array that this line will appear
 * @param salesTax A list of sales tax rates. Used to set the tax rate on lines
 * @param lineItemsIncludeTax `true` if tax is included, `false` if excluded.
 * @param items A dictionary of all items that line could be linked to
 * @returns The transformed line
 */
const mergeLines = (
  invoiceLine: IInvoiceLine,
  poLine: IPurchaseOrderLine,
  index: number,
  salesTax: SalesTax[],
  lineItemsIncludeTax: boolean,
  items: Record<string, IItem>
): UiInvoiceLine => {
  return {
    ...invoiceLineToGridInvoiceLine(
      invoiceLine,
      index,
      salesTax,
      lineItemsIncludeTax
    ),
    code: poLine.itemCode,
    description: poLine.itemId ? items[poLine.itemId]?.name : '',
    linkedItemId: poLine.itemId,
    brand: poLine.brandName,
    linkedBrandId: poLine.brandId,
    unit: poLine.unitName,
    linkedUnitId: poLine.unitId,
    quantityOrdered: poLine.quantityOrdered,
    isPurchaseOrderLine: true,
    isInvoiceLine: true,
    lineClass: () => '',
  }
}

/**
 * Function to get the icon to display on the invoice line columns
 * @param data - the system values to check against
 * @param linkedId - the linked id to check against the system values
 * @param value - the selected value
 * @param lineId - the ID of invoice line
 * @param initalInvoiceLines - the initial invoice lines
 * @param matchedOnCode - flag that let us know if the line is currently matched on code
 * @param matchedProperty - matched property to check in the initial invoice lines
 * @returns an icon to display or undefined
 */
export const getIconToDisplayOnInvoiceLineColumns = (
  data: Record<string, IItem | IBrand | IUnit>,
  linkedId: string,
  value: string,
  lineId: string,
  initalInvoiceLines: InvoiceLine[],
  matchedOnCode: boolean = false,
  matchedProperty: 'item' | 'brand' | 'unit',
  isPurchaseOrderLine: boolean = false
) => {
  const matchedInitialLine = initalInvoiceLines.find((l) => l.id === lineId)
  if (
    !data[linkedId] &&
    !isPurchaseOrderLine &&
    matchedProperty === 'brand' &&
    value &&
    value !== null
  )
    return NewIcon
  else if (
    !data[linkedId] &&
    !isPurchaseOrderLine &&
    matchedProperty !== 'brand'
  )
    return NewIcon
  else if (
    (matchedOnCode &&
      matchedProperty &&
      matchedInitialLine?.brand &&
      value !== matchedInitialLine?.[matchedProperty]) ||
    (matchedInitialLine?.brand && value !== matchedInitialLine?.brand)
  )
    return InfoIcon
}

/**
 * Function that finds a variant by code and supplierId
 * @param items items to search in
 * @param code code to search for
 * @param supplierId supplierId to filter by
 * @returns IItemSupplierVariant | undefined
 */
export function findSupplierVariantByCode(
  items: Record<string, IItem>,
  code: string,
  supplierId: string
): IItemSupplierVariant | undefined {
  // Iterate over each item in the items record
  for (const itemKey in items) {
    const item = items[itemKey]

    // If the item has no suppliers, skip it
    if (!item.suppliers) continue

    // Filter the supplier variants that match the given supplierId and stockCode
    const matchingVariants = item.suppliers.filter(
      (variant) =>
        variant.supplierId === supplierId && variant.stockCode === code
    )

    if (matchingVariants.length > 0) {
      // Check if any variant has defaultForSupplier set to true
      const defaultVariant = matchingVariants.find(
        (variant) => variant.defaultForSupplier === true
      )

      // Return the default variant if found, otherwise return the first matching variant
      return defaultVariant || matchingVariants[0]
    }
  }

  // Return undefined if no matching variant is found
  return undefined
}

// Helper to construct a list of options for an LoadedAutocomplete that has
// the original value as the first option.
export const getOptionsWithOriginalValue = (
  options: SelectOption[],
  lineId: string,
  field: string,
  addGroupingValue: boolean = true,
  initialInvoiceLines: InvoiceLine[] = []
) => {
  return [
    // We insert all scanned values but dont make them visible unless
    // the current line matches to the intial line. This is to help
    // with when lines are reordered. This is an easy way stop warnings
    // popping in the console saying the text on the autocomplete is not a
    // valid option when lines are re-ordered. Change if this becomes a
    // performance issue.
    ...initialInvoiceLines.map((line) => ({
      key: line['key'] ?? window.crypto.randomUUID(),
      label: line?.[field] ?? 'Not Set',
      value: line?.[field] ?? null,
      groupingValue: 'Original',
      display: line.id === lineId,
    })),
    ...(addGroupingValue
      ? options.map((option) => ({ ...option, groupingValue: 'All' }))
      : options),
  ]
}

/**
 * Checks a Grid Row returns whether the row should be highlighted
 * @param supplierId the supplierId used to match the variant
 * @param stockItemId the stockItemId used to match the variant
 * @param items the list of the items that will be used to search the variant
 * @returns ItemSupplierVariant | undefined
 */
export const getDefaultSupplierVariant = (
  supplierId: string,
  stockItemId: string,
  items: Record<string, IItem>
): ItemSupplierVariant | undefined => {
  const supplierVariant = items[stockItemId].suppliers?.find(
    (variant) =>
      variant.supplierId === supplierId &&
      variant.stockItemId === stockItemId &&
      variant.defaultForSupplier
  )

  return supplierVariant
}

/**
 * Checks a Grid Row returns whether the row should be highlighted
 * @param row the row to check
 * @param getValues the getValues function from the useForm hook
 * @returns true if row should be highlighted, false if not
 */
export const highlightRow = (row: UiInvoiceLine) => {
  if (!row || row.isPurchaseOrderLine) return false

  return !row.linkedItemId || !row.linkedUnitId
}

const strikethroughRow = (row: UiInvoiceLine) => {
  return !!row.deletedAt
}

const invoiceRowClass = (row: UiInvoiceLine) => {
  if (strikethroughRow(row)) return 'strikethrough'
  if (highlightRow(row)) return 'errorHighlight'
  return ''
}

/**
 * Checks whether the Invoice line wayfinding message should be displayed
 * @param invoiceLines the invoice lines to check
 * @returns true if the message should be displayed, false otherwise
 */
export const shouldDisplayLinesWarning = (
  invoiceLines: IInvoiceLine[]
): boolean => {
  return invoiceLines.some(
    (line) =>
      !line.deletedAt &&
      (!line.linkedItemId ||
        !line.linkedUnitId ||
        (line.brand && !line.linkedBrandId))
  )
}

/**
 * Builds the Invoice line wayfinding message by looking at all invoice lines
 * @param invoiceLines the invoice lines to check
 * @returns the invoice line wayfinding message
 */
export const buildLinesWarningMessage = (
  invoiceLines: IInvoiceLine[]
): string => {
  const propsToDisplay = {
    item: false,
    brand: false,
    unit: false,
  }

  // Check each line to see which properties are missing
  for (const line of invoiceLines) {
    // Don't check deleted lines
    if (line?.deletedAt) continue

    if (line?.linkedItemId == null) {
      propsToDisplay.item = true
    }

    if (line?.brand && !line?.linkedBrandId) {
      propsToDisplay.brand = true
    }

    if (line?.linkedUnitId == null) {
      propsToDisplay.unit = true
    }

    // Stop iterating if all properties are set to true as we know what to display. Improves efficiency.
    if (propsToDisplay?.item && propsToDisplay?.brand && propsToDisplay?.unit) {
      break
    }
  }

  const propTextConnectedWithAnd =
    buildPropsToDisplayForLinesMessage(propsToDisplay)

  return `Check you've matched the ${propTextConnectedWithAnd} for each invoice line to your Loaded ${propTextConnectedWithAnd}. Loaded will remember what you match, so it’s quicker and easier next time.`
}

/**
 * Build the message to display in the tooltip for a line when the line has missing properties
 * @param line the line to build the tooltip message for
 * @returns the message to display in the tooltip
 */
export const buildLineTooltipMessage = (line: IInvoiceLine): string => {
  const propsToDisplay = {
    item: false,
    brand: false,
    unit: false,
  }

  // Don't check deleted lines
  if (line.deletedAt) {
    return ''
  }

  if (line.linkedItemId == null) {
    propsToDisplay.item = true
  }

  if (line.brand && !line.linkedBrandId) {
    propsToDisplay.brand = true
  }

  if (line.linkedUnitId == null) {
    propsToDisplay.unit = true
  }

  const propText = buildPropsToDisplayForLinesMessage(propsToDisplay)
  const multipleProps = Object.values(propsToDisplay).filter(Boolean).length > 1

  return `This invoice line has a ${propText} which is not setup in Loaded. Please select a different ${propText} or setup a new one${
    multipleProps ? 's' : ''
  }.`
}

/**
 * Helper method that builds the text of which properties to display in a lines error message
 * @param propsToDisplay object representing the properties to display
 * @returns the text of which properties to display in the error message
 */
const buildPropsToDisplayForLinesMessage = (
  propsToDisplay: {
    item: boolean
    brand: boolean
    unit: boolean
  },
  conjunctionWord = 'and'
): string => {
  // Create array of words that should be displayed based on true flags
  const errorTerms = [
    propsToDisplay.item && 'stock item',
    propsToDisplay.brand && 'brand',
    propsToDisplay.unit && 'unit',
  ].filter(Boolean) as string[]

  if (errorTerms.length === 0) return ''

  // Join terms with appropriate grammar
  const propText =
    errorTerms.length === 1
      ? errorTerms[0] // Single term
      : errorTerms.length === 2
      ? `${errorTerms[0]} ${conjunctionWord} ${errorTerms[1]}` // Two terms
      : `${errorTerms.slice(0, -1).join(', ')}, ${conjunctionWord} ${
          errorTerms[errorTerms.length - 1]
        }` // Three terms

  return propText
}

/**
 * Extracts variants that are not the default for a supplier from invoice lines in preparation for
 * the Default for Supplier dialog that pops when receiving an invoice
 * @param invoice the invoice being received
 * @param items the items state
 * @param getItemSupplierVariant method that gets item supplier variants from the backend
 * @returns All variants received in the invoice that are not the default for the supplier
 */
export const extractNonDefaultVariantsFromInvoiceLines = async (
  invoice: IInvoice,
  items: Record<string, IItem> | null,
  getItemSupplierVariant: (
    code: string,
    itemId: string,
    supplierId: string
  ) => Promise<IItemSupplierVariant | null>
): Promise<IItemSupplierVariant[]> => {
  if (!items) return []
  const nonDefaultVariants: IItemSupplierVariant[] = []

  const getVariantPromises: Promise<IItemSupplierVariant | null>[] = []

  for (const line of invoice.lines!) {
    // Based off the AC of LOADED-5647. We only care about lines that have a quantity received
    // grater than 0.
    if ((line?.quantityReceived ?? 0) < 1 || line?.deletedAt) continue

    //We can't currently call the getItemSupplierVariant endpoint for lines witn no code
    if (!line?.code) continue

    if (!line.linkedItemId) {
      throw Error(
        'Received line did not have a linkedItemId. This should not be possible.'
      )
    }

    const item = items[line.linkedItemId]
    const variantsOfSupplier = item.suppliers?.filter(
      (v) => v.supplierId === invoice.linkedSupplierId
    )
    const matchingVariant = variantsOfSupplier?.find(
      (v) =>
        v.stockCode === line.code &&
        v.brandId === line.linkedBrandId &&
        v.unitId === line.linkedUnitId
    )

    // Only want variants that are not the default, when the item already has other variants.
    // If the item has no variants than this variant being received will automatically become the default.
    if (variantsOfSupplier && variantsOfSupplier.length > 0) {
      if (!matchingVariant) {
        getVariantPromises.push(
          getItemSupplierVariant(
            line.code!,
            line.linkedItemId,
            invoice.linkedSupplierId!
          )
        )
        continue
      } else if (!matchingVariant.defaultForSupplier) {
        nonDefaultVariants.push({
          ...matchingVariant,
          unitCost: line.unitCost,
        })
      }
    }
  }

  const newVariants = await Promise.all(getVariantPromises)

  for (const variant of newVariants) {
    if (!variant) continue // This scenario should not be possible

    nonDefaultVariants.push({
      ...variant,
      unitCost: invoice.lines!.find(
        (l) =>
          l.linkedItemId === variant.stockItemId && l.code === variant.stockCode
      )?.unitCost,
    })
  }

  return nonDefaultVariants
}

const getDefaultForSupplierLocalStorageKey = (
  companyId: string,
  userId: string
) =>
  `@@loaded/dialog/dismissed/${companyId}/${userId}/default-supplier-for-variant`

const getPriceAlertLocalStorageKey = (companyId: string, userId: string) =>
  `@@loaded/dialog/dismissed/${companyId}/${userId}/invoice-price-alert`

/**
 * Checks to see if the currently signed in user has disabled the default supplier dialog from showing
 * @param userId the current users Id
 * @returns true if the dialog should be displayed, false if not
 */
export const enabledForUserDefaultForSupplierDialog = (
  companyId: string,
  userId: string
) => {
  const key = getDefaultForSupplierLocalStorageKey(companyId, userId)
  return window.localStorage.getItem(key) !== 'true'
}

/**
 * Sets the value of the flag that determines whether default supplier dialog should be displayed
 * @param userId the ID of the current user
 * @param value the value to set the flag to (true to not display, false to display)
 */
export const setEnabledForUserDefaultForSupplierDialog = (
  companyId: string,
  userId: string,
  value: boolean
) => {
  const key = getDefaultForSupplierLocalStorageKey(companyId, userId)
  window.localStorage.setItem(key, `${value}`)
}

/**
 * Checks to see if the currently signed in user has disabled the price alert dialog from showing
 * @param userId the current users Id
 * @returns true if the dialog should be displayed, false if not
 */
export const enabledForUserPriceAlertDialog = (
  companyId: string,
  userId: string
) => {
  const key = getPriceAlertLocalStorageKey(companyId, userId)
  return window.localStorage.getItem(key) !== 'true'
}

/**
 * Sets the value of the flag that determines whether price alert dialog should be displayed
 * @param userId the ID of the current user
 * @param value the value to set the flag to (true to not display, false to display)
 */
export const setEnabledForUserPriceAlertDialog = (
  companyId: string,
  userId: string,
  value: boolean
) => {
  const key = getPriceAlertLocalStorageKey(companyId, userId)
  window.localStorage.setItem(key, `${value}`)
}

/**
 * Calculates the difference between two item costs.
 * @param currentPriceIncludesTax  true if the current price is tax inclusive, otherwise false.
 * @param previousCost An <tt>IStockCost</tt> object containing the last cost; the current price will be compared to this.
 * @param currentUnitRatio THe unit ration of the unit used by the current price
 * @param taxRate The tax rate of the current price, as a decimal (e.g. 0.15 for a rate of 15%)
 * @param currentPrice The current price
 * @returns An object containing:
 *   <tt>priceDifferenceExTax</tt>: The price difference excluding tax of one base unit.
 *   <tt>priceDifferenceExTaxPercentage</tt>: The price difference as a percentage of the previous price
 *    <tt>previousEquavilentPrice</tt>: The previous price expressed in the units used by the current price (excluding tax)
 */
export const calculatePriceDifference = (
  currentPriceIncludesTax: boolean,
  previousCost?: IStockCost,
  currentUnitRatio?: number,
  taxRate?: number,
  currentPrice?: number
) => {
  if (
    !currentUnitRatio ||
    !previousCost ||
    previousCost.documentType == DocumentType.None
  ) {
    return null
  }

  const normaliseByRatio = (price: number = 0, ratio: number = 1) =>
    price / ratio
  const roundTo2dp = (n: number) => parseFloat(n.toFixed(2))

  const currentTaxDivisor = currentPriceIncludesTax ? 1 + (taxRate ?? 0) : 1

  const currentPriceExcludingTax = (currentPrice ?? 0) / currentTaxDivisor

  const currentPricePerBaseUnitExTax = normaliseByRatio(
    currentPriceExcludingTax,
    currentUnitRatio
  )

  const previousPricePerBaseUnitExTax = previousCost?.cost ?? 0

  const priceDifferenceExTax =
    currentPricePerBaseUnitExTax - previousPricePerBaseUnitExTax

  if (roundTo2dp(priceDifferenceExTax) == 0) {
    return null
  }

  const priceDifferenceExTaxPercentage =
    (Math.abs(priceDifferenceExTax) / previousPricePerBaseUnitExTax) * 100

  const previousTaxMultipiler = currentPriceIncludesTax
    ? 1 + (previousCost?.salesTaxRate ?? 0)
    : 1

  const previousEquavilentPrice =
    previousPricePerBaseUnitExTax *
    previousTaxMultipiler *
    (currentUnitRatio ?? 1)

  return {
    priceDifferenceExTax,
    priceDifferenceExTaxPercentage,
    previousEquavilentPrice,
  }
}

/**
 * Handles invoice save operations, determining whether to create a new invoice or update an existing one.
 * If the invoice doesn't have an ID, it's treated as a new invoice from a purchase order.
 * @returns Object with updatedInvoice and isNewInvoice flag
 */
export const handleInvoiceSaveOperation = async (
  transformedInvoice: IInvoice,
  addInvoice: (
    invoice: IInvoice,
    successMessage: string,
    errorMessage: string
  ) => Promise<IInvoice | null>,
  updateInvoice: (
    invoice: IInvoice,
    successMessage: string,
    errorMessage: string
  ) => Promise<IInvoice | null>,
  createdBy: string,
  successMessage: string = 'Invoice processed successfully',
  errorMessage: string = 'Failed to process the invoice'
): Promise<{
  updatedInvoice: IInvoice | null
  isNewInvoice: boolean
}> => {
  // If the invoice doesn't have an ID, it's a new invoice from a purchase order
  if (!transformedInvoice.id) {
    // Create a new invoice with required metadata
    const newInvoice: IInvoice = {
      ...transformedInvoice,
      createdAt: dayjs(),
      reconciled: false,
      type: DocumentType.Invoice,
      version: InvoiceVersion.Current,
    }

    const updatedInvoice = await addInvoice(
      newInvoice,
      successMessage,
      errorMessage
    )

    return { updatedInvoice, isNewInvoice: true }
  } else {
    // Update existing invoice
    const updatedInvoice = await updateInvoice(
      transformedInvoice,
      successMessage,
      errorMessage
    )

    return { updatedInvoice, isNewInvoice: false }
  }
}

/**
 * Fetches estimated costs for invoice lines asynchronously and updates them in the store.
 * Sets loading states and handles errors gracefully.
 * @param normalizedLines - Dictionary of lines to fetch costs for
 * @param receivedDate - Date to use for cost calculations (as Dayjs object)
 * @param getLatestItemCosts - Function to fetch latest item costs
 * @param updateInvoiceLine - Function to update individual lines
 * @param setAwaitingCost - Function to set global awaiting cost state
 */
export const fetchAndUpdateLineCosts = async (
  normalizedLines: Record<string, UiInvoiceLine>,
  receivedDate: dayjs.Dayjs,
  getLatestItemCosts: (
    receivedDate: dayjs.Dayjs,
    ids: string[]
  ) => Promise<{ [key: string]: IStockCost } | null>,
  updateInvoiceLine: (
    lineId: string,
    updatedFields: Partial<UiInvoiceLine>
  ) => UiInvoiceLine,
  setAwaitingCost: (awaiting: boolean) => void
): Promise<void> => {
  const linesToFetchCosts = Object.values(normalizedLines).filter(
    (line) => line.linkedItemId
  )

  // Set awaiting cost state if we have lines to fetch costs for
  if (linesToFetchCosts.length > 0) {
    setAwaitingCost(true)
  }

  try {
    // Get all unique item IDs
    const itemIds = linesToFetchCosts
      .map((line) => line.linkedItemId!)
      .filter((id, index, self) => self.indexOf(id) === index) // Remove duplicates

    // Fetch costs for all items at once
    const latestItemCosts = await getLatestItemCosts(dayjs(), itemIds)

    if (latestItemCosts === null) {
      setAwaitingCost(false)
      return
    }

    // Update each line with its corresponding cost
    linesToFetchCosts.forEach((line) => {
      try {
        const itemStockCost = latestItemCosts[line.linkedItemId!]
        const estimatedCost = itemStockCost?.cost ?? 0

        // Update the line with the estimated cost
        const quantityReceived = line.quantityReceived ?? 0
        const saleTaxRate = line.saleTaxRate ?? 0
        const unitRatio = line.linkedUnitRatio ?? 1

        // Apply unit ratio to convert the estimated cost to the correct unit
        const adjustedUnitCost = roundNumber(estimatedCost * unitRatio, 2)
        const totalCost = roundNumber(adjustedUnitCost * quantityReceived, 2)

        // Calculate tax on the total cost using the helper function
        const taxAmount = calculateTaxOnItem(
          adjustedUnitCost,
          quantityReceived,
          saleTaxRate,
          false // unitCostIncludesTax - assuming false for cost calculations
        )

        updateInvoiceLine(line.id, {
          unitCost: adjustedUnitCost,
          totalCost: totalCost,
          taxAmount: roundNumber(taxAmount, 2),
          isNew: false, // Remove loading state once cost is fetched
        })
      } catch (error) {
        // Still remove loading state for this line on error
        updateInvoiceLine(line.id, { isNew: false })
        exceptionMessageHandler(
          error,
          `Failed to fetch cost for item ${line.linkedItemId}:`
        )
      }
    })
  } catch (error) {
    // Handle global error and remove loading state for all lines
    linesToFetchCosts.forEach((line) => {
      updateInvoiceLine(line.id, { isNew: false })
    })
    exceptionMessageHandler(error, 'Failed to fetch item costs')
  } finally {
    setAwaitingCost(false)
  }
}
